/**
 * Release 2 G5 — the editable list view's cell model (pure): which columns edit, how a cell reads as text (display,
 * editor seed, copy), how typed or pasted text parses, and which single / bulk request writes it. A multi-row fill or
 * paste of one value is always one bulk request (`bulkAssetAction` or `bulkSetAssetRights`).
 */
import type { ApprovalStatus, Asset, AssetCard, CustomField } from '@/lib/api/types';
import { APPROVAL_TONE } from '@/components/Misc';
import { parseTagNames } from '@/features/asset-detail/tagNames';
import { dateInputToExpiry, expiryToDateInput } from '@/features/rights/status';

export type EditableKind = 'title' | 'approval' | 'tags' | 'rights' | 'field';
export type ColumnKind = EditableKind | 'select' | 'thumb' | 'type' | 'size' | 'added';
export type ListColumn = { key: string; kind: ColumnKind; label: string; field?: CustomField; className?: string };
export type FieldValueBody = { value_text?: string; value_number?: number; value_date?: string; value_option_ids?: string[] };

/** A parsed cell value; `null` clears (rights expiry, custom fields). */
export type CellValue =
  | { kind: 'title'; title: string }
  | { kind: 'approval'; status: ApprovalStatus }
  | { kind: 'tags'; names: string[] }
  | { kind: 'rights'; expires_at: string | null }
  | { kind: 'field'; field: CustomField; value: FieldValueBody | null };

export const tagsOf = (c: AssetCard): string[] => c.tag_names ?? [];
export const APPROVAL_STATUSES: ApprovalStatus[] = ['none', 'in_progress', 'needs_review', 'approved'];
export const isEditable = (k: ColumnKind): k is EditableKind => k === 'title' || k === 'approval' || k === 'tags' || k === 'rights' || k === 'field';

/** Text of a cell (what the editor starts from and what copy puts on the clipboard). */
export function cellText(col: ListColumn, card: AssetCard, asset?: Asset | null): string {
  switch (col.kind) {
    case 'title':
      return card.title;
    case 'approval':
      return APPROVAL_TONE[card.approval_status].label;
    case 'tags':
      return tagsOf(card).join(', ');
    case 'rights':
      return expiryToDateInput(card.rights_expires_at);
    case 'field': {
      const f = col.field!;
      const v = asset?.custom_field_values.find((x) => x.field_id === f.id);
      if (!v) return '';
      if (f.type === 'select' || f.type === 'multi_select') return (v.value_option_ids ?? []).map((id) => f.options?.find((o) => o.id === id)?.label ?? id).join(', ');
      if (f.type === 'number') return v.value_number != null ? String(v.value_number) : '';
      if (f.type === 'date') return v.value_date?.slice(0, 10) ?? '';
      return v.value_text ?? '';
    }
    default:
      return '';
  }
}

function parseDate(text: string): string | null | undefined {
  const t = text.trim();
  if (!t) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? undefined : expiryToDateInput(d.toISOString());
}

/** Parse typed or pasted text for a column; `{ error }` when it does not fit. */
export function parseCell(col: ListColumn, text: string): { value: CellValue } | { error: string } {
  const t = text.trim();
  switch (col.kind) {
    case 'title':
      return t ? { value: { kind: 'title', title: t } } : { error: 'A title cannot be empty' };
    case 'approval': {
      const low = t.toLowerCase().replace(/[\s-]+/g, '_');
      const status = APPROVAL_STATUSES.find((s) => s === low || APPROVAL_TONE[s].label.toLowerCase() === t.toLowerCase()) ?? (t === '' ? 'none' : undefined);
      return status ? { value: { kind: 'approval', status } } : { error: `“${t}” is not an approval status` };
    }
    case 'tags': {
      const names = parseTagNames(t);
      return names.length ? { value: { kind: 'tags', names } } : { error: 'No tag names' };
    }
    case 'rights': {
      const d = parseDate(t);
      if (d === undefined) return { error: `“${t}” is not a date` };
      return { value: { kind: 'rights', expires_at: d ? dateInputToExpiry(d) : null } };
    }
    case 'field': {
      const f = col.field!;
      if (!t) return { value: { kind: 'field', field: f, value: null } };
      if (f.type === 'number') return Number.isFinite(Number(t)) ? { value: { kind: 'field', field: f, value: { value_number: Number(t) } } } : { error: `“${t}” is not a number` };
      if (f.type === 'date') {
        const d = parseDate(t);
        return d ? { value: { kind: 'field', field: f, value: { value_date: d } } } : { error: `“${t}” is not a date` };
      }
      if (f.type === 'select' || f.type === 'multi_select') {
        const labels = f.type === 'select' ? [t] : t.split(',').map((s) => s.trim()).filter(Boolean);
        const ids: string[] = [];
        for (const l of labels) {
          const o = f.options?.find((x) => x.label.toLowerCase() === l.toLowerCase() || x.id === l);
          if (!o) return { error: `“${l}” is not an option of ${f.name}` };
          ids.push(o.id);
        }
        return { value: { kind: 'field', field: f, value: { value_option_ids: ids } } };
      }
      return { value: { kind: 'field', field: f, value: { value_text: t } } };
    }
    default:
      return { error: 'This column cannot be edited' };
  }
}

/** The one request that writes `value` to many assets, or why there is none. */
export type BulkPlan =
  | { op: 'bulk'; action: 'set_approval' | 'add_tags' | 'set_custom_field'; params: Record<string, unknown> }
  | { op: 'rights'; rights: { expires_at: string | null } }
  | { op: 'none'; reason: string };

export function bulkPlan(value: CellValue): BulkPlan {
  switch (value.kind) {
    case 'approval':
      return { op: 'bulk', action: 'set_approval', params: { status: value.status } };
    case 'tags':
      return { op: 'bulk', action: 'add_tags', params: { names: value.names } };
    case 'rights':
      return { op: 'rights', rights: { expires_at: value.expires_at } };
    case 'field':
      return value.value ? { op: 'bulk', action: 'set_custom_field', params: { field_id: value.field.id, value: value.value } } : { op: 'none', reason: 'An empty field cannot be filled into several assets' };
    case 'title':
      return { op: 'none', reason: 'Titles are edited one asset at a time' };
  }
}

/** Value of a source cell for a fill (Ctrl+D), read from the card (and the asset detail for custom fields). */
export function sourceValue(col: ListColumn, card: AssetCard, asset?: Asset | null): CellValue | null {
  switch (col.kind) {
    case 'approval':
      return { kind: 'approval', status: card.approval_status };
    case 'tags':
      return tagsOf(card).length ? { kind: 'tags', names: tagsOf(card) } : null;
    case 'rights':
      return { kind: 'rights', expires_at: card.rights_expires_at ?? null };
    case 'title':
      return { kind: 'title', title: card.title };
    case 'field': {
      if (!asset) return null;
      const r = parseCell(col, cellText(col, card, asset));
      return 'value' in r ? r.value : null;
    }
    default:
      return null;
  }
}
